import * as SwitchPrimitive from "@radix-ui/react-switch";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { Loader2 } from "lucide-react";
import { forwardRef, useEffect, useState, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from "react";
import { useChrome } from "@/lib/chrome";

/** Class names, with later Tailwind classes overriding earlier ones (w-36 beats w-full). */
export function cn(...v: ClassValue[]) {
  return twMerge(clsx(v));
}

// ── Button ──────────────────────────────────────────────────────────────────
type Variant = "primary" | "secondary" | "ghost" | "danger" | "outline" | "live" | "success";
type Size = "xs" | "sm" | "md" | "icon" | "icon-sm";

const variants: Record<Variant, string> = {
  primary: "bg-accent text-white hover:brightness-110 active:brightness-95 shadow-sm",
  secondary: "bg-panel-3 text-fg hover:bg-line border border-line",
  ghost: "text-muted hover:text-fg hover:bg-panel-3",
  outline: "border border-line-strong text-fg hover:bg-panel-3",
  danger: "bg-down text-white hover:brightness-110 active:brightness-95 shadow-sm",
  success: "bg-up text-white hover:brightness-110 active:brightness-95 shadow-sm",
  live: "bg-live text-white hover:brightness-110 shadow-sm",
};
const sizes: Record<Size, string> = {
  xs: "h-6 px-2 text-[11.5px] gap-1 rounded-md",
  sm: "h-7 px-2.5 text-[12.5px] gap-1.5 rounded-md",
  md: "h-9 px-3.5 text-[13px] gap-2 rounded-lg",
  icon: "h-9 w-9 rounded-lg",
  "icon-sm": "h-7 w-7 rounded-md",
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  icon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "secondary", size = "md", loading, icon, className, children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      className={cn(
        "inline-flex shrink-0 select-none items-center justify-center font-medium whitespace-nowrap transition-[background,filter,color] disabled:pointer-events-none disabled:opacity-45",
        variants[variant],
        sizes[size],
        className,
      )}
      disabled={disabled || loading}
      {...rest}
    >
      {loading ? <Loader2 className="size-3.5 animate-spin" /> : icon}
      {children}
    </button>
  );
});

// ── Card ────────────────────────────────────────────────────────────────────
export function Card({ className, children, ...rest }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn("rounded-xl border border-line bg-panel shadow-panel", className)} {...rest}>
      {children}
    </div>
  );
}

export function CardHeader({ title, subtitle, actions, icon, className }: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  icon?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex min-h-11 items-center gap-2.5 border-b border-line px-4 py-2", className)}>
      {icon && <span className="text-muted">{icon}</span>}
      <div className="min-w-0 flex-1">
        <div className="truncate text-[13px] font-semibold tracking-tight">{title}</div>
        {subtitle && <div className="line-clamp-2 text-[11.5px] text-muted md:line-clamp-none md:truncate">{subtitle}</div>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-1.5">{actions}</div>}
    </div>
  );
}

// ── Badge / status ──────────────────────────────────────────────────────────
export type Tone = "neutral" | "up" | "down" | "warn" | "accent" | "live" | "muted";
const tones: Record<Tone, string> = {
  neutral: "bg-panel-3 text-fg border-line",
  up: "bg-up-soft text-up border-transparent",
  down: "bg-down-soft text-down border-transparent",
  warn: "bg-warn-soft text-warn border-transparent",
  accent: "bg-accent-soft text-accent border-transparent",
  live: "bg-live-soft text-live border-transparent",
  muted: "bg-transparent text-muted border-line",
};

export function Badge({ tone = "neutral", className, children }: { tone?: Tone; className?: string; children: ReactNode }) {
  return (
    <span className={cn("inline-flex h-5 shrink-0 items-center gap-1 rounded-md border px-1.5 text-[11px] font-medium whitespace-nowrap", tones[tone], className)}>
      {children}
    </span>
  );
}

const dotTones: Record<Tone, string> = {
  neutral: "bg-faint",
  up: "bg-up",
  down: "bg-down",
  warn: "bg-warn",
  accent: "bg-accent",
  live: "bg-live",
  muted: "bg-faint",
};

export function Dot({ tone = "neutral", pulse, className }: { tone?: Tone; pulse?: boolean; className?: string }) {
  return <span className={cn("inline-block size-2 shrink-0 rounded-full", dotTones[tone], pulse && "pulse-dot", className)} />;
}

// ── Form controls ───────────────────────────────────────────────────────────
const field =
  "w-full rounded-lg border border-line bg-panel-2 px-2.5 text-[13px] text-fg placeholder:text-faint transition-colors hover:border-line-strong focus:border-accent focus:outline-none disabled:opacity-50";

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }>(function Input(
  { className, invalid, ...rest },
  ref,
) {
  return <input ref={ref} className={cn(field, "h-9", invalid && "border-down", className)} {...rest} />;
});

/** A number field that keeps what is typed while it is being typed. A plain
 * controlled ``type="number"`` input turns a partial entry ("-", "1e", "")
 * into 0 and writes "0" back, so a negative threshold could not be typed.
 * ``onValue`` gets only finite numbers (``undefined`` once cleared, when
 * ``allowEmpty``). */
export function NumberInput({ value, onValue, int, signed, allowEmpty, ...rest }: Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "onChange" | "type"> & {
  value: number | null | undefined;
  onValue: (v: number | undefined) => void;
  int?: boolean;
  /** may be negative: a phone's numeric keypad has no minus key, so show the full keyboard */
  signed?: boolean;
  allowEmpty?: boolean;
  invalid?: boolean;
}) {
  const shown = value === null || value === undefined || Number.isNaN(value) ? "" : String(value);
  const [text, setText] = useState(shown);
  useEffect(() => {
    // a change from outside (a reset, a recipe): show it, unless it is what is typed
    setText((t) => (t.trim() !== "" && Number(t) === value ? t : shown));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shown]);
  return (
    <Input
      type="text"
      inputMode={signed ? "text" : int ? "numeric" : "decimal"}
      value={text}
      onChange={(e) => {
        const t = e.target.value;
        setText(t);
        if (t.trim() === "") {
          if (allowEmpty) onValue(undefined);
          return;
        }
        const n = int ? Number.parseInt(t, 10) : Number(t);
        if (Number.isFinite(n) && (!int || /^-?\d+$/.test(t.trim()))) onValue(n);
      }}
      {...rest}
    />
  );
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea(
  { className, ...rest },
  ref,
) {
  return <textarea ref={ref} className={cn(field, "py-2 leading-relaxed", className)} {...rest} />;
});

export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select className={cn(field, "h-9 cursor-pointer appearance-none bg-[length:12px] bg-[right_0.6rem_center] bg-no-repeat pr-7", className)} style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%238d96a8' stroke-width='2.5'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")` }} {...rest}>
      {children}
    </select>
  );
}

export function Field({ label, hint, error, children, className, inline }: {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  children: ReactNode;
  className?: string;
  inline?: boolean;
}) {
  // the hint sits outside the <label> so it is not read as part of the field's name
  return (
    <div className={cn("flex flex-col gap-1", className)}>
      <label className={cn(inline ? "flex items-center justify-between gap-4" : "flex flex-col gap-1.5")}>
        {label && <span className="text-[12px] font-medium text-muted">{label}</span>}
        {children}
      </label>
      {error ? <span className="text-[11.5px] text-down">{error}</span> : hint ? <span className="text-[11.5px] text-faint">{hint}</span> : null}
    </div>
  );
}

export function Switch({ checked, onChange, disabled, label, className }: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  label?: string;
  className?: string;
}) {
  return (
    <SwitchPrimitive.Root
      checked={checked}
      onCheckedChange={onChange}
      disabled={disabled}
      aria-label={label}
      className={cn(
        "relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border border-transparent transition-colors disabled:cursor-not-allowed disabled:opacity-50",
        checked ? "bg-accent" : "bg-line-strong",
        className,
      )}
    >
      <SwitchPrimitive.Thumb className="block size-4 translate-x-0.5 rounded-full bg-white shadow transition-transform data-[state=checked]:translate-x-[18px]" />
    </SwitchPrimitive.Root>
  );
}

export function Segmented<T extends string>({ value, options, onChange, size = "sm", className }: {
  value: T;
  options: { value: T; label: ReactNode; tone?: "live" }[];
  onChange: (v: T) => void;
  size?: "xs" | "sm";
  className?: string;
}) {
  return (
    <div className={cn("inline-flex rounded-lg border border-line bg-panel-2 p-0.5", className)} role="tablist">
      {options.map((o) => (
        <button
          key={o.value}
          role="tab"
          aria-selected={value === o.value}
          onClick={() => onChange(o.value)}
          className={cn(
            "rounded-md px-2.5 font-medium whitespace-nowrap transition-colors",
            size === "xs" ? "h-6 text-[11.5px]" : "h-7 text-[12.5px]",
            value === o.value
              ? o.tone === "live"
                ? "bg-live text-white shadow-sm"
                : "bg-panel-3 text-fg shadow-sm"
              : "text-muted hover:text-fg",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ── Feedback ────────────────────────────────────────────────────────────────
export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={cn("size-4 animate-spin text-muted", className)} />;
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn("animate-pulse rounded-md bg-panel-3", className)} />;
}

export function Empty({ icon, title, children, action, className }: {
  icon?: ReactNode;
  title: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col items-center justify-center gap-2 px-6 py-10 text-center", className)}>
      {icon && <div className="mb-1 grid size-10 place-items-center rounded-xl bg-panel-3 text-muted">{icon}</div>}
      <div className="text-[13.5px] font-semibold">{title}</div>
      {children && <div className="max-w-sm text-[12.5px] text-muted">{children}</div>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

export function Callout({ tone = "accent", title, children, icon, className }: {
  tone?: Tone;
  title?: ReactNode;
  children?: ReactNode;
  icon?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex gap-2.5 rounded-lg border px-3 py-2.5 text-[12.5px]", tones[tone], className)}>
      {icon && <span className="mt-0.5 shrink-0">{icon}</span>}
      <div className="min-w-0">
        {title && <div className="font-semibold">{title}</div>}
        {children && <div className={cn(title && "mt-0.5", "text-fg/85")}>{children}</div>}
      </div>
    </div>
  );
}

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="rounded border border-line bg-panel-3 px-1.5 py-0.5 font-mono text-[10.5px] text-muted">{children}</kbd>;
}

export function Section({ title, description, children, actions, className }: {
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn("space-y-3", className)}>
      <div className="flex items-end justify-between gap-3">
        <div>
          <h3 className="text-[13.5px] font-semibold">{title}</h3>
          {description && <p className="mt-0.5 text-[12.5px] text-muted">{description}</p>}
        </div>
        {actions}
      </div>
      {children}
    </section>
  );
}

/** A page's title, description and actions. On a phone the title is large and
 * the top bar picks it up once it scrolls away; the description is clamped and
 * the actions become one row that scrolls sideways. */
/** A page's large title on a phone: the top bar shows the text once the
 * returned ref's element has scrolled under it (the native large-title pattern). */
export function useLargeTitle(text: string | null) {
  const setTitle = useChrome((s) => s.setTitle);
  const setOnScreen = useChrome((s) => s.setTitleOnScreen);
  const [el, setEl] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setTitle(text);
    return () => setTitle(null);
  }, [text, setTitle]);
  useEffect(() => {
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(([e]) => setOnScreen(e.isIntersecting), { rootMargin: "-56px 0px 0px 0px" });
    io.observe(el);
    return () => {
      io.disconnect();
      setOnScreen(false);
    };
  }, [el, setOnScreen]);
  return setEl;
}

export function PageHeader({ title, description, actions, children, leading }: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  /** Before the title (a back button). */
  leading?: ReactNode;
}) {
  const ref = useLargeTitle(typeof title === "string" ? title : null);
  return (
    <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
      <div className="flex min-w-0 items-start gap-2">
        {leading}
        <div className="min-w-0">
          <h1 ref={ref} className="text-[24px] leading-tight font-bold tracking-tight md:text-[19px] md:font-semibold">
            {title}
          </h1>
          {description && <p className="mt-1 line-clamp-2 max-w-2xl text-[13px] text-muted md:mt-0.5 md:line-clamp-none">{description}</p>}
          {children}
        </div>
      </div>
      {actions && (
        <div className="no-scrollbar -mx-4 flex items-center gap-2 overflow-x-auto px-4 md:mx-0 md:flex-wrap md:overflow-visible md:px-0 lg:justify-end">
          {actions}
        </div>
      )}
    </div>
  );
}
